import {
	ArrowLeftOutlined,
	EnvironmentOutlined,
	PlusOutlined,
	TeamOutlined,
	UserAddOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Button, Card, Descriptions, Skeleton, Tag } from "antd";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { StaffFormModal } from "@/modules/staff/components/StaffFormModal";
import { StaffTable } from "@/modules/staff/components/StaffTable";
import { useStaffStore } from "@/modules/staff/store/staff.store";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { BrigadeAssignStaffModal } from "../components/BrigadeAssignStaffModal";
import { BrigadeEquipmentTable } from "../components/BrigadeEquipmentTable";
import { BrigadeTaskTable } from "../components/BrigadeTaskTable";
import { BrigadeTerritoryMap } from "../components/BrigadeTerritoryMap";
import { useBrigade } from "../hooks/useBrigade";
import type { BrigadeTerritory } from "../types";

function TerritoryTags({ territories }: { territories?: BrigadeTerritory[] }) {
	if (!territories || territories.length === 0) {
		return <>—</>;
	}
	return (
		<div className="flex flex-wrap gap-1">
			{territories.map((t) => (
				<Tag key={t.id} color="green">
					{t.name}
				</Tag>
			))}
		</div>
	);
}

export function BrigadeDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	// Staff sub-table pagination lives in the URL (prefixed: sibling tables share this route)
	const { page, limit, setPage } = useUrlTableState({ prefix: "staff" });

	const { data: brigade, isLoading } = useBrigade(id ?? "");
	const openCreateStaff = useStaffStore((s) => s.openCreateModal);
	const [assignStaffOpen, setAssignStaffOpen] = useState(false);

	if (!id) {
		return <Navigate to="/organizations" replace />;
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<Button
						type="text"
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(-1)}
						size="small"
						className="text-ink-soft"
					/>
					{isLoading ? (
						<Skeleton.Input active size="small" style={{ width: 180 }} />
					) : (
						<h4 className="text-lg font-semibold m-0">{brigade?.name ?? "Brigada"}</h4>
					)}
				</div>
				{brigade && (
					<div className="flex items-center gap-2">
						<Button icon={<UserAddOutlined />} onClick={() => setAssignStaffOpen(true)}>
							Mavjud xodimni biriktirish
						</Button>
						<Button type="primary" icon={<PlusOutlined />} onClick={openCreateStaff}>
							Xodim qo'shish
						</Button>
					</div>
				)}
			</div>

			<Card size="small">
				{isLoading ? (
					<Skeleton active paragraph={{ rows: 2 }} />
				) : brigade ? (
					<Descriptions
						size="small"
						column={2}
						items={[
							{
								key: "leader",
								label: (
									<span className="flex items-center gap-1">
										<UserOutlined /> Rahbar
									</span>
								),
								children: brigade.leaderName ? <Tag color="blue">{brigade.leaderName}</Tag> : "—",
							},
							{
								key: "territories",
								label: (
									<span className="flex items-center gap-1">
										<EnvironmentOutlined /> Hududlar
									</span>
								),
								children: <TerritoryTags territories={brigade.territories} />,
							},
							{
								key: "members",
								label: (
									<span className="flex items-center gap-1">
										<TeamOutlined /> A'zolar soni
									</span>
								),
								children: <Tag>{brigade.memberCount ?? 0}</Tag>,
							},
						]}
					/>
				) : null}
			</Card>

			{brigade && brigade.territories.length > 0 && (
				<Card
					size="small"
					title={
						<span className="font-semibold flex items-center gap-1">
							<EnvironmentOutlined /> Hudud xaritasi
						</span>
					}
				>
					<BrigadeTerritoryMap territoryIds={brigade.territories.map((t) => t.id)} />
				</Card>
			)}

			{brigade && (
				<>
					<Card size="small" title={<span className="font-semibold">Xodimlar</span>}>
						<StaffTable
							organizationId={brigade.organizationId}
							brigadeId={brigade.id}
							page={page}
							limit={limit}
							onPageChange={setPage}
						/>
					</Card>
					<Card size="small" title={<span className="font-semibold">Texnikalar</span>}>
						<BrigadeEquipmentTable organizationId={brigade.organizationId} brigadeId={brigade.id} />
					</Card>
					<Card size="small" title={<span className="font-semibold">Vazifalar</span>}>
						<BrigadeTaskTable organizationId={brigade.organizationId} brigadeId={brigade.id} />
					</Card>
					<StaffFormModal
						organizationId={brigade.organizationId}
						organizations={[]}
						brigadeId={brigade.id}
					/>
					<BrigadeAssignStaffModal
						brigade={brigade}
						open={assignStaffOpen}
						onClose={() => setAssignStaffOpen(false)}
					/>
				</>
			)}
		</div>
	);
}
